import { ACTION_LABEL } from "@/lib/i18nLabels";
import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import Combobox from "@/components/Combobox";
import ClientAutocomplete from "@/components/ClientAutocomplete";
import RupiahInput from "@/components/RupiahInput";
import PPNBreakdown from "@/components/PPNBreakdown";
import { INDONESIA_REGIONS, PROVINCES } from "@/lib/indonesia";
import api, { formatDetail } from "@/lib/api";

const emptyNP = { project_name: "", client: "", city: "", province: "", start_date: "", end_date: "", contract_value: "", retention_pct: 0, retention_period_days: 0, retention_type: "inside", vo_aggregation: "separate", is_ppn_exempt: false };

const toIso = (d) => (d ? new Date(d).toISOString() : null);

export default function NewProjectForm({ parents, onDone, onCancel }) {
  const [np, setNp] = useState(emptyNP);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/active-projects", {
        project_type: "new_project",
        project_name: np.project_name,
        client: np.client,
        city: np.city || null,
        province: np.province || null,
        start_date: toIso(np.start_date),
        end_date: toIso(np.end_date),
        contract_value: np.contract_value ? parseFloat(np.contract_value) : null,
        retention_pct: parseFloat(np.retention_pct) || 0,
        retention_period_days: parseInt(np.retention_period_days, 10) || 0,
        retention_type: np.retention_type || "inside",
        vo_aggregation: np.vo_aggregation || "separate",
        is_ppn_exempt: !!np.is_ppn_exempt,
      });
      toast.success("Project created");
      onDone?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Project Name</Label>
        <Input data-testid="proj-name" required value={np.project_name} onChange={(e) => setNp({ ...np, project_name: e.target.value })} className="rounded-none" />
      </div>
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Client</Label>
        <ClientAutocomplete
          testid="proj-client"
          required
          value={np.client}
          onChange={(v) => setNp({ ...np, client: v })}
          options={parents.map((p) => p.client || "")}
          placeholder="Ketik / pilih dari client sebelumnya…"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Province</Label>
          <Combobox testid="proj-province" value={np.province} onChange={(v) => setNp({ ...np, province: v, city: "" })} options={PROVINCES} placeholder="Select province…" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">City / Regency</Label>
          <Combobox testid="proj-city" value={np.city} onChange={(v) => setNp({ ...np, city: v })} options={np.province ? INDONESIA_REGIONS[np.province] || [] : []} placeholder={np.province ? "Select city…" : "Pick province first"} disabled={!np.province} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Start Date</Label>
          <Input data-testid="proj-start" type="date" required value={np.start_date} onChange={(e) => setNp({ ...np, start_date: e.target.value })} className="rounded-none" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">End Date <span className="normal-case text-slate-400 text-[9px]">(opsional)</span></Label>
          <Input data-testid="proj-end" type="date" value={np.end_date} onChange={(e) => setNp({ ...np, end_date: e.target.value })} className="rounded-none" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Contract Value (IDR) <span className="normal-case text-slate-400 text-[9px]">(DPP)</span></Label>
          <RupiahInput testid="proj-contract" value={np.contract_value} onChange={(v) => setNp({ ...np, contract_value: v })} placeholder="0" />
          <label className="flex items-center gap-1.5 text-[10px] cursor-pointer select-none" data-testid="proj-non-ppn-toggle">
            <input type="checkbox" checked={!!np.is_ppn_exempt} onChange={(e) => setNp({ ...np, is_ppn_exempt: e.target.checked })} className="h-3 w-3 accent-[#FF5A1F]" />
            <span className="uppercase tracking-wider font-semibold text-slate-600">Project Non-PPN (bebas PPN 11%)</span>
          </label>
          <PPNBreakdown value={np.contract_value} isExempt={!!np.is_ppn_exempt} testid="proj-ppn-breakdown" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Retention</Label>
          <div className="inline-flex border border-slate-300 text-xs w-full" data-testid="proj-retention">
            {[{ v: 0, label: "None" }, { v: 5, label: "5%" }, { v: 10, label: "10%" }].map((r) => (
              <button key={r.v} type="button" data-testid={`proj-retention-${r.v}`} onClick={() => setNp({ ...np, retention_pct: r.v })}
                className={`flex-1 px-3 py-2 uppercase tracking-wider font-semibold ${np.retention_pct === r.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {r.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-500">Held back each billing entry.</p>
        </div>
      </div>
      {Number(np.retention_pct) > 0 && (
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Jenis Retensi</Label>
          <div className="inline-flex border border-slate-300 text-xs w-full" data-testid="proj-retention-type">
            {[{ v: "inside", label: "Ditahan per penagihan" }, { v: "outside", label: "Ditahan pada akhir penagihan" }].map((r) => (
              <button key={r.v} type="button" data-testid={`proj-retention-type-${r.v}`}
                onClick={() => setNp({ ...np, retention_type: r.v })}
                className={`flex-1 px-3 py-2 uppercase tracking-wider font-semibold ${(np.retention_type || "inside") === r.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {r.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 leading-relaxed">
            <b className="text-[#0B1D3A]">Ditahan per penagihan</b>: retensi dipotong dari setiap invoice → Received Net = Invoice − PPh − Retensi.<br/>
            <b className="text-[#0B1D3A]">Ditahan pada akhir penagihan</b>: setiap invoice bebas potongan (max billing = kontrak × (100−r)%). Retensi ditagih terpisah di invoice terakhir.
          </p>
        </div>
      )}
      {Number(np.retention_pct) > 0 && (
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Retention Period (Masa Retensi)</Label>
          <div className="relative">
            <Input data-testid="proj-retention-period-days" type="number" min="0" max="3650" step="1"
              value={np.retention_period_days ?? 0}
              onChange={(e) => setNp({ ...np, retention_period_days: e.target.value })}
              className="rounded-none pr-14 tabular-nums" placeholder="0" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">Hari</span>
          </div>
          <p className="text-[10px] text-slate-500">
            Ketik durasi dalam <span className="font-semibold">hari</span> (0 = belum ditentukan).
            {Number(np.retention_period_days) > 0 && (
              <span className="ml-1 text-amber-700 font-mono">≈ {(Number(np.retention_period_days) / 30).toFixed(1)} bulan</span>
            )}
          </p>
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Perhitungan VO</Label>
        <div className="inline-flex border border-slate-300 text-xs w-full" data-testid="proj-vo-aggregation">
          {[{ v: "separate", label: "VO Dipisah" }, { v: "combined", label: "VO Digabung" }].map((r) => (
            <button key={r.v} type="button" data-testid={`proj-vo-agg-${r.v}`}
              onClick={() => setNp({ ...np, vo_aggregation: r.v })}
              className={`flex-1 px-3 py-2 uppercase tracking-wider font-semibold ${(np.vo_aggregation || "separate") === r.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {r.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-slate-500 leading-relaxed">
          <b className="text-[#0B1D3A]">VO Dipisah</b>: setiap VO/WO punya ledger sendiri (default).<br/>
          <b className="text-[#0B1D3A]">VO Digabung</b>: uang masuk & keluar semua VO/WO digabung ke parent; limit tagihan = total kontrak parent + semua VO.
        </p>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" className="rounded-none" onClick={onCancel}>{ACTION_LABEL.cancel}</Button>
        <Button type="submit" data-testid="proj-submit" disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]">
          {busy ? "Menyimpan…" : "Simpan"}
        </Button>
      </DialogFooter>
    </form>
  );
}
